import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
    Box,
    Button,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TextField,
    Typography,
    alpha,
} from "@mui/material";
import Popup from "../../../components/Popup";
import { StyledTableCell, StyledTableRow } from "../../../components/styles";
import { getApiBaseUrl } from "../../../config/api";
import {
    TermRecordsScopeAlert,
    TermRecordsScopeTabs,
} from "../../../components/term/TermRecordsScopeTabs";
import { useTermRecordsScope } from "../../../hooks/useTermRecordsScope";
import { buildTermRecordsQuery } from "../../../utils/termRecordsScope";

const STATUS_OPTIONS = ["All", "Submitted", "Approved", "Rejected", "Draft"];
const STATUS_CHIP = {
    Draft: "default",
    Submitted: "info",
    Approved: "success",
    Rejected: "error",
};

function formatDate(d) {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-GB");
}

function SubmissionReviewPanel({ type, schoolId, currentUser, recordsScope }) {
    const isMarks = type === "marks";
    const listUrl = isMarks
        ? `${getApiBaseUrl()}/MarksSubmissions/school/${schoolId}`
        : `${getApiBaseUrl()}/AttendanceSubmissions/school/${schoolId}`;
    const approveUrl = (id) =>
        isMarks
            ? `${getApiBaseUrl()}/MarksSubmission/${id}/approve`
            : `${getApiBaseUrl()}/AttendanceSubmission/${id}/approve`;
    const rejectUrl = (id) =>
        isMarks
            ? `${getApiBaseUrl()}/MarksSubmission/${id}/reject`
            : `${getApiBaseUrl()}/AttendanceSubmission/${id}/reject`;

    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [statusFilter, setStatusFilter] = useState("Submitted");
    const [detail, setDetail] = useState(null);
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectId, setRejectId] = useState(null);
    const [rejectionNote, setRejectionNote] = useState("");
    const [actionLoading, setActionLoading] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");

    const load = useCallback(async () => {
        if (!schoolId) return;
        setLoading(true);
        try {
            const params = buildTermRecordsQuery({
                recordsScope,
                extra: statusFilter !== "All" ? { status: statusFilter } : {},
            });
            const { data } = await axios.get(listUrl, { params });
            setRows(Array.isArray(data) ? data : []);
        } catch {
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [schoolId, statusFilter, listUrl, recordsScope]);

    useEffect(() => {
        load();
    }, [load]);

    const handleApprove = async (id) => {
        setActionLoading(true);
        try {
            const { data } = await axios.put(approveUrl(id), { approvedById: currentUser._id });
            setMessage(data?.message && data.status !== "Approved" ? data.message : "Approved successfully");
            setShowPopup(true);
            setDetail(null);
            load();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not approve");
            setShowPopup(true);
        } finally {
            setActionLoading(false);
        }
    };

    const handleReject = async () => {
        if (!rejectId) return;
        setActionLoading(true);
        try {
            await axios.put(rejectUrl(rejectId), { rejectionNote });
            setMessage("Submission rejected");
            setShowPopup(true);
            setRejectOpen(false);
            setDetail(null);
            load();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not reject");
            setShowPopup(true);
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <>
            <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: 1, borderColor: "divider" }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Status</InputLabel>
                    <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        {STATUS_OPTIONS.map((s) => (
                            <MenuItem key={s} value={s}>
                                {s}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Paper>

            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: 1, borderColor: "divider" }}>
                <Table size="small">
                    <TableHead>
                        <StyledTableRow>
                            <StyledTableCell>Teacher</StyledTableCell>
                            <StyledTableCell>Class</StyledTableCell>
                            <StyledTableCell>Subject</StyledTableCell>
                            <StyledTableCell>{isMarks ? "Assessment" : "Date"}</StyledTableCell>
                            <StyledTableCell>Students</StyledTableCell>
                            <StyledTableCell>Status</StyledTableCell>
                            <StyledTableCell align="right">Actions</StyledTableCell>
                        </StyledTableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            [...Array(3)].map((_, i) => (
                                <StyledTableRow key={i}>
                                    <StyledTableCell colSpan={7}>
                                        <Skeleton height={32} />
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))
                        ) : rows.length === 0 ? (
                            <StyledTableRow>
                                <StyledTableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                    No submissions match this filter.
                                </StyledTableCell>
                            </StyledTableRow>
                        ) : (
                            rows.map((row) => {
                                const id = row.id || row._id;
                                const pending = row.status === "Submitted";
                                return (
                                    <StyledTableRow key={id}>
                                        <StyledTableCell>{row.teacher?.name || "—"}</StyledTableCell>
                                        <StyledTableCell>{row.class?.sclassName || "—"}</StyledTableCell>
                                        <StyledTableCell>{row.subject?.subName || "—"}</StyledTableCell>
                                        <StyledTableCell>
                                            {isMarks
                                                ? `${row.examLabel || "—"} · ${row.term || ""} ${row.academicYear || ""}`
                                                : formatDate(row.sessionDate)}
                                        </StyledTableCell>
                                        <StyledTableCell>{row.items?.length ?? 0}</StyledTableCell>
                                        <StyledTableCell>
                                            <Chip
                                                size="small"
                                                label={row.status}
                                                color={STATUS_CHIP[row.status] || "default"}
                                            />
                                        </StyledTableCell>
                                        <StyledTableCell align="right">
                                            <Button size="small" onClick={() => setDetail(row)}>
                                                View
                                            </Button>
                                            {pending ? (
                                                <>
                                                    <Button
                                                        size="small"
                                                        color="success"
                                                        variant="contained"
                                                        sx={{ ml: 0.5 }}
                                                        disabled={actionLoading}
                                                        onClick={() => handleApprove(id)}
                                                    >
                                                        Approve
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        color="error"
                                                        variant="outlined"
                                                        sx={{ ml: 0.5 }}
                                                        disabled={actionLoading}
                                                        onClick={() => {
                                                            setRejectId(id);
                                                            setRejectionNote("");
                                                            setRejectOpen(true);
                                                        }}
                                                    >
                                                        Reject
                                                    </Button>
                                                </>
                                            ) : null}
                                        </StyledTableCell>
                                    </StyledTableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {isMarks ? "Marks submission" : "Attendance submission"} — {detail?.teacher?.name}
                </DialogTitle>
                <DialogContent dividers>
                    {detail?.items?.map((item) => (
                        <Stack
                            key={item.id || item.studentId}
                            direction="row"
                            justifyContent="space-between"
                            sx={{ py: 0.75, borderBottom: 1, borderColor: "divider" }}
                        >
                            <Typography variant="body2">
                                {item.student?.name || item.studentId}{" "}
                                <Typography component="span" variant="caption" color="text.secondary">
                                    (Roll {item.student?.rollNum ?? "—"})
                                </Typography>
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                                {isMarks ? `${item.marksObtained}%` : item.status}
                            </Typography>
                        </Stack>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetail(null)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={rejectOpen} onClose={() => !actionLoading && setRejectOpen(false)}>
                <DialogTitle>Rejection note</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="Reason for rejection"
                        value={rejectionNote}
                        onChange={(e) => setRejectionNote(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectOpen(false)} disabled={actionLoading}>
                        Cancel
                    </Button>
                    <Button color="error" variant="contained" onClick={handleReject} disabled={actionLoading}>
                        Reject
                    </Button>
                </DialogActions>
            </Dialog>

            <Popup message={message} showPopup={showPopup} setShowPopup={setShowPopup} />
        </>
    );
}

const SubmissionReview = ({ mode }) => {
    const { currentUser } = useSelector((s) => s.user);
    const schoolId = currentUser?._id;
    const [tab, setTab] = useState(0);
    const { recordsScope, setRecordsScope, currentTermLabel } = useTermRecordsScope(schoolId);

    const isMarksOnly = mode === "marks";
    const isAttendanceOnly = mode === "attendance";
    const isSplit = isMarksOnly || isAttendanceOnly;

    const title = isMarksOnly
        ? "Marks review"
        : isAttendanceOnly
          ? "Attendance review"
          : "Marks & attendance review";
    const subtitle = isMarksOnly
        ? "Approve teacher marks submissions before they update official student records."
        : isAttendanceOnly
          ? "Approve teacher attendance submissions before they update official student records."
          : "Approve teacher submissions before they update official student records.";

    return (
        <Box
            sx={{
                minHeight: "100%",
                py: { xs: 2, sm: 3 },
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.04)} 0%, ${t.palette.grey[100]} 20%)`
                        : alpha(t.palette.common.black, 0.12),
            }}
        >
            <Container maxWidth="lg">
                <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
                    {title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {subtitle}
                </Typography>

                <TermRecordsScopeTabs value={recordsScope} onChange={setRecordsScope} />
                <TermRecordsScopeAlert
                    scope={recordsScope}
                    currentTermLabel={currentTermLabel}
                    sectionLabel={isMarksOnly ? "marks submissions" : isAttendanceOnly ? "attendance submissions" : "submissions"}
                />

                {!isSplit && (
                    <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
                        <Tab label="Marks / results" />
                        <Tab label="Attendance" />
                    </Tabs>
                )}

                {isMarksOnly || (!isSplit && tab === 0) ? (
                    <SubmissionReviewPanel
                        type="marks"
                        schoolId={schoolId}
                        currentUser={currentUser}
                        recordsScope={recordsScope}
                    />
                ) : (
                    <SubmissionReviewPanel
                        type="attendance"
                        schoolId={schoolId}
                        currentUser={currentUser}
                        recordsScope={recordsScope}
                    />
                )}
            </Container>
        </Box>
    );
};

export default SubmissionReview;
